import { gql } from '@apollo/client';

import { ErrorsAttributes } from '../constants';

export const LABEL_OBJECT_TYPES = ['site', 'group', 'task'];
export const DEFAULT_LABEL_BACKGROUND_COLOR = '#e0e0e0';
export const LABEL_HEX_PATTERN = /^#([0-9A-Fa-f]{3}){1,2}$/;

export const labelStateDefaults = {
  name: '',
  description: '',
  backgroundColor: DEFAULT_LABEL_BACKGROUND_COLOR,
  objectType: null,
};

export const isLabelObjectType = (value) => LABEL_OBJECT_TYPES.includes(value);

export const labelObjectTypeModelKey = (objectType) => {
  if (objectType === 'site') return 'site.singular';
  if (objectType === 'group') return 'group.singular';
  return 'task.singular';
};

export const labelObjectTypePluralModelKey = (objectType) => {
  if (objectType === 'site') return 'site.plural';
  if (objectType === 'group') return 'group.plural';
  return 'task.plural';
};

const expandHexDigits = (hex) => {
  const digits = (hex || '').replace('#', '');
  if (digits.length === 3) return digits.split('').map((char) => char + char).join('');
  return digits;
};

export const expandLabelHex = (hex) => {
  if (!LABEL_HEX_PATTERN.test(hex || '')) return DEFAULT_LABEL_BACKGROUND_COLOR;
  return `#${expandHexDigits(hex)}`;
};

const linearize = (channel) => (
  channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
);

export const getLabelTextColor = (hex) => {
  const digits = expandHexDigits(expandLabelHex(hex));
  if (!/^[0-9A-Fa-f]{6}$/.test(digits)) return 'dark';

  const [r, g, b] = digits.match(/../g).map((part) => parseInt(part, 16) / 255);
  const luminance = 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
  return luminance > 0.179 ? 'dark' : 'light';
};

export const LabelFragment = gql`
  fragment LabelFragment on Label {
    id
    name
    description
    backgroundColor
    textColor
    objectType
  }
`;

export const LabelsQuery = gql`
  query LabelsQuery($accountId: String!, $objectType: LabelObjectType, $page: Int!, $perPage: Int!, $sortBy: LabelsSortBy!, $sortOrder: SortOrder!, $search: String) {
    labels(accountId: $accountId, objectType: $objectType, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        ...LabelFragment
      }
    }
  }
  ${LabelFragment}
`;

export const LabelsQueryForSelection = gql`
  query LabelsForSelection($accountId: String!, $objectType: LabelObjectType!, $page: Int!, $perPage: Int!, $sortBy: LabelsSortBy!, $sortOrder: SortOrder!, $search: String) {
    labels(accountId: $accountId, objectType: $objectType, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search) {
      totalEntries
      totalPages
      currentPage
      nodes {
        ...LabelFragment
      }
    }
  }
  ${LabelFragment}
`;

export const LabelQuery = gql`
  query LabelQuery($accountId: String!, $labelId: String!) {
    label(accountId: $accountId, labelId: $labelId) {
      ...LabelFragment
    }
  }
  ${LabelFragment}
`;

export const CreateLabelMutation = gql`
  mutation CreateLabel($accountId: String!, $objectType: LabelObjectType!, $name: String!, $description: String, $backgroundColor: String) {
    createLabel(accountId: $accountId, objectType: $objectType, name: $name, description: $description, backgroundColor: $backgroundColor) {
      label {
        ...LabelFragment
        ${ErrorsAttributes}
      }
    }
  }
  ${LabelFragment}
`;

export const UpdateLabelMutation = gql`
  mutation UpdateLabel($accountId: String!, $labelId: String!, $name: String, $description: String, $backgroundColor: String) {
    updateLabel(accountId: $accountId, labelId: $labelId, name: $name, description: $description, backgroundColor: $backgroundColor) {
      label {
        ...LabelFragment
        ${ErrorsAttributes}
      }
    }
  }
  ${LabelFragment}
`;

export const DestroyLabelsMutation = gql`
  mutation DestroyLabels($accountId: String!, $labelIds: [String!]!) {
    destroyLabels(accountId: $accountId, labelIds: $labelIds) {
      status
    }
  }
`;
